import { ReactElement } from 'react';
import NumberFormat from 'react-number-format';
import { FieldInputProps } from 'formik';
import cn from 'classnames';

import config from '../../config.json';
import styles from './TextInput.module.scss';
import { Loader } from '../Loader/Loader';
import { getDataTest } from '../../utils/dataTests';

export interface TextInputProps {
    field: FieldInputProps<string | number>;
    id: string;
    name: string;
    variant?: 'text' | 'number' | 'price' | 'email' | 'decimal';
    suffix?: string;
    min?: number | string; // string is for date range
    max?: number | string;
    placeholder?: string;
    className?: string;
    autoFocus?: boolean;
    error?: string;
    loading?: boolean;
    readonly?: boolean;
    thousandSeparator?: string;
}

export const TextInput = ({
    field,
    className,
    placeholder,
    suffix,
    min = 0,
    max = 9999999999,
    variant = 'text',
    autoFocus,
    error,
    loading = false,
    readonly,
    thousandSeparator = ' ',
    ...props
}: TextInputProps): ReactElement | null => {
    return (
        <div className={cn(styles.wrapper, className)} data-test={getDataTest(props)}>
            <div className={styles.loading}>
                {loading && (
                    <div className={styles.loader} data-test={getDataTest(props, 'Loader')}>
                        <Loader size="small" />
                    </div>
                )}
                {variant === config.INPUT_FIELD_TYPES.PRICE ||
                variant === config.INPUT_FIELD_TYPES.NUMBER ||
                variant === config.INPUT_FIELD_TYPES.DECIMAL ? (
                    <NumberFormat
                        className={cn(styles.input, suffix && styles.inputWithSuffix, error && styles.hasError)}
                        allowNegative={false}
                        fixedDecimalScale={false}
                        decimalScale={variant === config.INPUT_FIELD_TYPES.DECIMAL ? undefined : 0}
                        value={
                            field.value != null && field.value !== ''
                                ? field.value.toString().replace(/^0+([1-9])/, '$1')
                                : ''
                        }
                        name={field.name}
                        id={field.name}
                        onChange={field.onChange}
                        data-lpignore="true"
                        disabled={readonly}
                        allowLeadingZeros={false}
                        thousandSeparator={thousandSeparator}
                        decimalSeparator={variant === config.INPUT_FIELD_TYPES.DECIMAL ? ',' : undefined}
                        isNumericString={true}
                        placeholder={placeholder}
                        inputMode={
                            variant === config.INPUT_FIELD_TYPES.PRICE || variant === config.INPUT_FIELD_TYPES.DECIMAL
                                ? 'decimal'
                                : 'numeric'
                        }
                        isAllowed={({ floatValue, value }) => {
                            const removeLeadingZeros = (val: string | number): string =>
                                val.toString().replace(/^0+/, '') || '0';
                            const adjustedFloatValue = removeLeadingZeros(floatValue !== undefined ? floatValue : 0);
                            const adjustedValue = removeLeadingZeros(value !== undefined ? value : '');

                            // cisla a desetina cisla lze zadat jen v rozmezi min, max
                            // cenu neumoznit zadat zapornou, pro maximálni hodnotu pouzit INT_MAX (2 147 483 647),
                            // Number.MAX_SAFE_INTEGER vraci 9 007 199 254 740 992, coz nelze pouzit => pada backend
                            if (
                                variant === config.INPUT_FIELD_TYPES.NUMBER ||
                                variant === config.INPUT_FIELD_TYPES.DECIMAL
                            ) {
                                return (
                                    Number(min) <= parseFloat(adjustedFloatValue) &&
                                    parseFloat(adjustedFloatValue) <= Number(max)
                                );
                            }

                            if (variant === config.INPUT_FIELD_TYPES.PRICE) {
                                return (
                                    !(
                                        (Number(adjustedFloatValue) === 0 && adjustedValue.length > 1) ||
                                        (Array.from(adjustedValue)[0] === '0' && adjustedValue.length > 1)
                                    ) &&
                                    0 <= parseFloat(adjustedFloatValue) &&
                                    parseFloat(adjustedFloatValue) <= config.INT_MAX
                                );
                            }

                            return true;
                        }}
                        data-test={getDataTest(props, 'Input')}
                    />
                ) : (
                    // ### Regular text input
                    <input
                        data-lpignore="true"
                        className={cn(styles.input, suffix && styles.inputWithSuffix, error && styles.hasError)}
                        placeholder={placeholder}
                        type={variant}
                        name={field.name}
                        id={field.name}
                        value={field.value || ''}
                        max={max}
                        disabled={readonly}
                        onChange={field.onChange}
                        autoFocus={autoFocus}
                        data-test={getDataTest(props, 'Input')}
                    />
                )}
                {suffix && (
                    <span className={styles.suffix} data-test={getDataTest(props, 'Suffix')}>
                        {suffix}
                    </span>
                )}
            </div>
        </div>
    );
};
